import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import oktaAuth from './oktaAuth';
import { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { useDispatch, useSelector } from 'react-redux';
import { AuthActions } from 'common/auth/actions';
import { CILogger } from 'common/log/log.provider';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { RootState } from 'store';
import { AuthUserType } from 'common/graphql/types';
import { includes, replace, throttle, toLower } from 'lodash';
import { isUserLoggedIn } from 'common/auth/auth_route';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';
import { useTranslation } from 'react-i18next';
import { useClient } from 'common/hooks/use-client';
import { saveLastPagesOpenedAndLogout, timeoutConfig } from 'common/viewcomp/layout/helpers';
import { useFxIntegrationInitialization } from 'common/featureFlags/hooks/useFxIntegrationInitialization';

const isUserCurrentlyActive = (inactivityThresholdSec: number) => {
    const lastActivity = +(localStorage.getItem(timeoutConfig.storageKey) || 0);
    const now = Math.floor(Date.now() / 1000);

    return now - lastActivity < inactivityThresholdSec;
};

interface Props {
    children?: ReactNode;
}

const AuthStateMonitor = ({ children }: Props): ReactElement => {
    const { oktaAuth: oktaAuthInstance } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const client = useClient();
    const { t } = useTranslation('auth');
    const dispatch = useDispatch();
    const [sessionExpiration, setSessionExpiration] = useState<Date>(null);
    const fxIntegrationState = useFxIntegrationInitialization();
    const inactivityThreshold = timeoutConfig.totalSessionDuration - timeoutConfig.dialogDuration; // matches the time when SessionTimeoutDialog shows the warning dialog

    const isOktaLogin =
        featureFlags.fxIntegration &&
        auth?.activeUser?.userType !== AuthUserType.PATIENT &&
        auth?.activeUser?.authenticationMethod === 'OKTA';

    const handleLogout = useCallback(() => {
        oktaAuthInstance.tokenManager.clear();
        dispatch(AuthActions.logoutRequest());
    }, [oktaAuthInstance, dispatch]);

    const checkIfOktaSessionExistsAndSignOutIfNot = useCallback(async () => {
        if (auth.switchingUser) {
            return;
        }

        const sessionInfo = (await oktaAuthInstance.session.get()) as unknown as {
            login: string;
            status: string;
            expiresAt: string;
        };

        if (sessionInfo?.expiresAt) {
            setSessionExpiration(new Date(sessionInfo.expiresAt));
        }

        // double check the visibilityState as when leaving the page oktaSessiontExists may not be reliable
        if (document.visibilityState === 'hidden') {
            return;
        }

        if (!sessionInfo || sessionInfo.status !== 'ACTIVE') {
            CIPageStatus.error(t('login.sso.error.session_terminated'));
            CILogger.getInstance().info('User signed out because invalid okta session detected.');

            saveLastPagesOpenedAndLogout(auth, client, handleLogout);
        } else {
            const oktaLogin = sessionInfo?.login;
            const activeUserName = replace(auth?.activeUser?.name || '', /^FX_/, '');
            const isSameUser = toLower(oktaLogin) === toLower(activeUserName);

            // different user detected in okta, logout and sign in with new user
            if (!isSameUser && auth?.activeUser?.name) {
                CIPageStatus.error(t('login.sso.error.session_changed'));
                CILogger.getInstance().info(
                    `Different user detected in okta, logging out: ' + ${activeUserName} and signing in user: ' + ${oktaLogin}`
                );

                dispatch(AuthActions.logoutRequest());
                await oktaAuthInstance.signInWithRedirect();
            }
        }
    }, [auth, client, oktaAuthInstance, dispatch, handleLogout]);

    const throttledCheckIfOktaSessionExistsAndSignOutIfNot = useMemo(
        () => throttle(checkIfOktaSessionExistsAndSignOutIfNot, 10000),
        [checkIfOktaSessionExistsAndSignOutIfNot]
    );

    const checkIfOktaSessionExistsAndSignInIfYes = useCallback(async () => {
        if (isUserLoggedIn(auth) || includes(window.location.pathname, '/sso/okta') || auth.switchingUser) {
            return;
        }

        try {
            dispatch(AuthActions.setSSOLoading(true));

            const [oktaSessionExists, fxIntegrationEnabled] = await Promise.all([
                await oktaAuthInstance.session.exists(),
                await fxIntegrationState.waitForInitialization(),
            ]);

            if (oktaSessionExists && fxIntegrationEnabled) {
                await oktaAuthInstance.signInWithRedirect();
            } else {
                dispatch(AuthActions.setSSOLoading(false));
            }
        } catch (e) {
            dispatch(AuthActions.setSSOLoading(false));
            CILogger.getInstance().info('No SSO session detected', e);
        }
    }, [dispatch, oktaAuthInstance, auth, fxIntegrationState]);

    const checkOktaSession = useCallback(() => {
        if (isOktaLogin) {
            throttledCheckIfOktaSessionExistsAndSignOutIfNot();
        } else {
            checkIfOktaSessionExistsAndSignInIfYes();
        }
    }, [isOktaLogin, checkIfOktaSessionExistsAndSignInIfYes, throttledCheckIfOktaSessionExistsAndSignOutIfNot]);

    useEffect(
        function verifyOktaSessionOnpageReload() {
            if (featureFlags.fxIntegration) {
                checkOktaSession();
            }
        },
        [featureFlags.fxIntegration] // there is no dependency during signing out of user, it is meant only for page reload
    );

    useEffect(
        function verifyOktaSessionOnVisibilityChange() {
            if (featureFlags.fxIntegration) {
                const handleVisibilityChange = async () => {
                    if (document.visibilityState === 'visible') {
                        checkOktaSession();
                    }
                };

                document.addEventListener('visibilitychange', handleVisibilityChange);

                return () => {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                };
            }
        },
        [checkOktaSession, featureFlags.fxIntegration]
    );

    useEffect(
        function refreshSession() {
            if (!isOktaLogin || !isUserLoggedIn(auth) || !sessionExpiration) {
                return;
            }

            const sessionTimeoutMs = sessionExpiration.getTime() - Date.now();
            const keepAliveInterval = sessionTimeoutMs / 2;

            const intervalId = setInterval(async () => {
                const userIsActive = isUserCurrentlyActive(inactivityThreshold);
                if (userIsActive && document.visibilityState === 'visible') {
                    checkIfOktaSessionExistsAndSignOutIfNot();
                }
            }, keepAliveInterval);

            return () => clearInterval(intervalId);
        },
        [isOktaLogin, auth, checkIfOktaSessionExistsAndSignOutIfNot, sessionExpiration, inactivityThreshold]
    );

    useEffect(
        function initTokenManagerHandlers() {
            const handleTokenError = (error: any) => {
                CILogger.getInstance().error('Token renewal failed:', error);
                checkIfOktaSessionExistsAndSignOutIfNot();
            };

            const handleTokenRenewed = (key: string) => {
                CILogger.getInstance().info(`Token renewed: ${key}`);
            };

            oktaAuthInstance.tokenManager.on('error', handleTokenError);
            oktaAuthInstance.tokenManager.on('renewed', handleTokenRenewed);

            return () => {
                oktaAuthInstance.tokenManager.off('error', handleTokenError);
                oktaAuthInstance.tokenManager.off('renewed', handleTokenRenewed);
            };
        },
        [oktaAuthInstance, dispatch, checkIfOktaSessionExistsAndSignOutIfNot]
    );

    return <>{children}</>;
};

const OktaAuthProvider = ({ children }: Props): ReactElement => {
    const history = useHistory();

    const restoreOriginalUri: RestoreOriginalUriFunction = (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };

    return (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
            <AuthStateMonitor>{children}</AuthStateMonitor>
        </Security>
    );
};

export default OktaAuthProvider;
